/**
 * ButtonGroup Component
 * 
 * A horizontal group of selectable buttons for single-choice selection.
 * 
 * @param {string} label - Label text for the group
 * @param {string} description - Optional description below label
 * @param {*} value - Currently selected value
 * @param {function} onChange - Callback when selection changes
 * @param {Array} options - Array of options: [{id, label}] or simple values
 * @param {string} color - Accent color: "blue", "rose", "amber", "indigo" (default: "indigo")
 */

const COLOR_CLASSES = {
  blue: "bg-blue-600 text-white shadow-sm",
  rose: "bg-rose-600 text-white shadow-sm",
  amber: "bg-amber-600 text-white shadow-sm",
  indigo: "bg-indigo-600 text-white shadow-sm",
};

export default function ButtonGroup({
  label,
  description = null,
  value,
  onChange,
  options = [],
  color = "indigo",
}) {
  const activeClass = COLOR_CLASSES[color] || COLOR_CLASSES.indigo;

  // Normalize options to always have {id, label} format
  const normalizedOptions = options.map((opt) => {
    if (typeof opt === "object" && opt.id !== undefined) {
      return opt;
    }
    return { id: opt, label: String(opt) };
  });

  return (
    <div className="space-y-2">
      {label && (
        <label className="text-sm font-medium text-gray-700">{label}</label>
      )}
      {description && (
        <p className="text-gray-400 text-xs -mt-1">{description}</p>
      )}
      
      <div className="flex gap-2">
        {normalizedOptions.map((option) => (
          <button
            key={option.id}
            type="button"
            onClick={() => onChange(option.id)}
            className={`
              flex-1 py-2.5 rounded-lg font-medium text-sm transition-all duration-200
              ${value === option.id
                ? activeClass
                : "bg-gray-100 text-gray-600 hover:bg-gray-200"
              }
            `}
          >
            {option.label}
          </button>
        ))}
      </div>
    </div>
  );
}